import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  User,
  Currency,
  Phone,
  Mail,
  Building2,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Upload,
  Calendar,
  ExternalLink,
  Search,
  X,
  Filter,
  UserCheck,
  Users,
  ChevronDown,
  XCircle,
  AlertCircle,
  Banknote,
} from "lucide-react";
import api from "../services/api";
import toast from "react-hot-toast";
import HelpButton from "../components/HelpButton";
import { canShowDisbursementAction, shouldShowStageBadge } from "../constants/applicationStages";

function Applications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    perPage: 15,
    lastPage: 1,
    currentPage: 1,
  });

  // Search state
  const [search, setSearch] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [status, setStatus] = useState("");
  const [phone, setPhone] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedFeeCharges, setExpandedFeeCharges] = useState({});
  const [expandedPendingUsers, setExpandedPendingUsers] = useState({});
  const [expandedDisbursements, setExpandedDisbursements] = useState({});
  const [expandedPartnerFos, setExpandedPartnerFos] = useState({});
  
  // Cancel application state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [applicationToCancel, setApplicationToCancel] = useState(null);
  const [cancelRemarks, setCancelRemarks] = useState("");
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadApplications(currentPage);
  }, [currentPage]);

  const loadApplications = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        per_page: 15,
      };

      // Add search parameters if they exist
      if (search) params.search = search;
      if (applicationId) params.application_id = applicationId;
      if (status) params.status = status;
      if (phone) params.phone = phone;

      const response = await api.get("/api/mobile/applications", {
        params,
      });

      if (response.data.success) {
        setApplications(response.data.data.data);
        console.log({ response: response.data.data.data });
        setPagination({
          total: response.data.data.total,
          perPage: response.data.data.per_page,
          lastPage: response.data.data.last_page,
          currentPage: response.data.data.current_page,
        });
      }
    } catch (error) {
      console.error("Failed to load applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.lastPage) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadApplications(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setApplicationId("");
    setStatus("");
    setPhone("");
    setCurrentPage(1);
    loadApplications(1);
  };

  const hasActiveFilters = search || applicationId || status || phone;

  const handleCancelClick = (application) => {
    setApplicationToCancel(application);
    setCancelRemarks("");
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!cancelRemarks.trim()) {
      toast.error("Please enter remarks for cancellation");
      return;
    }

    setCancelling(true);
    try {
      const response = await api.post("/api/mobile/application/cancel", {
        application_id: applicationToCancel.id,
        remarks: cancelRemarks,
      });

      if (response.data.status) {
        toast.success(response.data.message || "Application cancelled successfully");
        setShowCancelModal(false);
        setApplicationToCancel(null);
        setCancelRemarks("");
        // Reload applications to reflect the change
        loadApplications(currentPage);
      } else {
        toast.error(response.data.message || "Failed to cancel application");
      }
    } catch (error) {
      console.error("Failed to cancel application:", error);
      toast.error(
        error.response?.data?.message || "Failed to cancel application"
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelModalClose = () => {
    if (!cancelling) {
      setShowCancelModal(false);
      setApplicationToCancel(null);
      setCancelRemarks("");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadgeColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-700";
    const statusLower = String(status).toLowerCase();
    if (statusLower.includes("approved") || statusLower.includes("disburs"))
      return "bg-green-100 text-green-700";
    if (statusLower.includes("pending") || statusLower.includes("progress"))
      return "bg-yellow-100 text-yellow-700";
    if (statusLower.includes("rejected") || statusLower.includes("cancel"))
      return "bg-red-100 text-red-700";
    return "bg-blue-100 text-blue-700";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg safe-top transition-colors duration-300">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors active:scale-95"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Applications
              </h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {pagination.total} Applications
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, ID, phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl text-sm font-semibold hover:shadow-lg transition-all active:scale-95"
              >
                Search
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all active:scale-95 flex items-center gap-2 ${
                  showFilters || hasActiveFilters
                    ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300"
                    : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                }`}
              >
                <Filter className="w-4 h-4" />
                {hasActiveFilters && (
                  <span className="w-2 h-2 bg-indigo-600 rounded-full" />
                )}
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
                      Application ID
                    </label>
                    <input
                      type="text"
                      placeholder="Enter ID"
                      value={applicationId}
                      onChange={(e) => setApplicationId(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="disbursed">Disbursed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      placeholder="Enter phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {hasActiveFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-semibold transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6 pb-32">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FileText className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              No Applications Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              There are no applications available at the moment.
            </p>
          </div>
        ) : (
          <>
            {/* Applications List */}
            <div className="space-y-4">
              {applications.map((application) => (
                <div
                  key={application.id}
                  className="bg-white dark:bg-gray-800 rounded-3xl p-5 shadow-lg hover:shadow-xl transition-all border border-gray-100 dark:border-gray-700"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4 gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {application.customer?.profile_category === 'non-individual'
                            ? application.customer?.profile_organization_name
                            : application.customer?.profile_category === 'sole-proprietor'
                            ? application.customer?.profile_prop_name
                            : <>{application.customer?.profile_first_name}{" "}{application.customer?.profile_last_name}</>}
                        </h3>
                        {application.customer?.profile_category && application.customer?.profile_category !== 'individual' && (
                          <p className="text-[10px] font-medium text-indigo-500 dark:text-indigo-400 capitalize -mt-0.5 mb-0.5">
                            {application.customer?.profile_category === 'non-individual' ? 'Non-Individual' : 'Sole Proprietor'}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          App ID: #{application.id}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {application.product && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/40 dark:to-indigo-900/40 rounded-full text-[10px] font-semibold text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                              <CreditCard className="w-3 h-3" />
                              {application.product.product_short_name ||
                                application.product.product_name}
                            </span>
                          )}
                          {application.app_partner_loan_id && (
                            <a
                              href={`${
                                import.meta.env.VITE_API_BASE_URL?.includes('galaxy.ecofy.co.in')
                                  ? "https://los.portal.autovertplug.com"
                                  : import.meta.env.VITE_API_BASE_URL?.includes('.pp.')
                                    ? "https://los.pp.portal.autovertplug.com"
                                    : "https://los.staging-portal-v2.autovertplug.com"
                              }/r/${application.app_partner_loan_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-indigo-700 dark:text-indigo-300 hover:opacity-70 transition-opacity"
                            >
                              <span>Partner: {application.app_partner_loan_id}</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/doc-upload/${application.id}`);
                        }}
                        className="px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-xs font-semibold hover:shadow-lg transition-all active:scale-95 flex items-center gap-1.5"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Upload
                      </button>
                      {application.app_status !== 'CANCELLED' && 
                       !String(application.app_current_status_final || '').toLowerCase().includes('cancel') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelClick(application);
                          }}
                          className="px-3 py-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl text-xs font-semibold hover:shadow-lg transition-all active:scale-95 flex items-center gap-1.5"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Status Badge */}
                  {(application.app_current_status_final ||
                    application.app_status) && (
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getStatusBadgeColor(
                          application.app_current_status_final ||
                            application.app_status,
                        )}`}
                      >
                        {application.app_current_status_final ||
                          application.app_status}
                      </span>
                      {shouldShowStageBadge(application) && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700/50">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {application.app_current_stage}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Customer Details */}
                  {(application.customer?.profile_phone || application.customer?.profile_email) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                      {application.customer?.profile_phone && (
                        <div className="flex items-center gap-2 text-xs">
                          <Phone className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                          <span className="text-gray-700 dark:text-gray-300">
                            {application.customer.profile_phone}
                          </span>
                        </div>
                      )}
                      {application.customer?.profile_email && (
                        <div className="flex items-center gap-2 text-xs">
                          <Mail className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                          <span className="text-gray-700 dark:text-gray-300 truncate">
                            {application.customer.profile_email}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Dealer & Asset Info */}
                  {(application.dealer || application.asset) && (
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {application.dealer && (
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-2">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Building2 className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                              Dealer
                            </span>
                          </div>
                          <p className="text-xs font-bold text-gray-900 dark:text-white">
                            {application.dealer.dealer_name}
                          </p>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400">
                            {application.dealer.dealer_code}
                          </p>
                        </div>
                      )}
                      {application.asset && (
                        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-xl p-2">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <CreditCard className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                              Asset
                            </span>
                          </div>
                          <p className="text-xs font-bold text-gray-900 dark:text-white break-words">
                            {application.asset.asset_name}
                          </p>
                          {application.asset.asset_capacity && (
                            <p className="text-[10px] text-gray-500 dark:text-gray-400">
                              {application.asset.asset_capacity} kW • {application.asset.asset_solar_type || application.asset.asset_type}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Loan Details */}
                  {(application.finaloffer || application.applicationdetails || application.scheme) && (
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-3 mb-3 border border-blue-100 dark:border-blue-800/30">
                      <div className="grid grid-cols-2 gap-2">
                        {application.applicationdetails?.app_loan_amount && (
                          <div>
                            <p className="text-[9px] font-semibold text-blue-600 dark:text-blue-400 mb-0.5">
                              Requested
                            </p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                              ₹{Number(application.applicationdetails.app_loan_amount).toLocaleString('en-IN')}
                            </p>
                          </div>
                        )}
                        {application.finaloffer?.offer_total_loan_amount && (
                          <div>
                            <p className="text-[9px] font-semibold text-green-600 dark:text-green-400 mb-0.5">
                              Approved
                            </p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                              ₹{Number(application.finaloffer.offer_total_loan_amount).toLocaleString('en-IN')}
                            </p>
                          </div>
                        )}
                        {application.scheme?.scheme_offer_rate && (
                          <div>
                            <p className="text-[9px] font-semibold text-purple-600 dark:text-purple-400 mb-0.5">
                              Rate {application.scheme.scheme_rate_type === 'flat' && '(Flat)'}
                            </p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                              {application.scheme.scheme_offer_rate}%
                            </p>
                          </div>
                        )}
                        {application.scheme?.scheme_tenure && (
                          <div>
                            <p className="text-[9px] font-semibold text-orange-600 dark:text-orange-400 mb-0.5">
                              Tenure
                            </p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                              {application.scheme.scheme_tenure} months
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Partner FOS Details */}
                  {application.lead &&
                    (application.lead.sourcing_channel_contact_name ||
                      application.lead.sourcing_channel_contact_phone ||
                      application.lead.sourcing_channel_contact_email) && (
                      <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/30 dark:to-blue-900/30 rounded-2xl p-3 mb-3 border border-sky-100 dark:border-sky-800/30">
                        <button
                          onClick={() =>
                            setExpandedPartnerFos((prev) => ({
                              ...prev,
                              [application.id]: !prev[application.id],
                            }))
                          }
                          className="flex items-center justify-between w-full gap-2"
                        >
                          <div className="flex items-center gap-2">
                            <UserCheck className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                              Partner FOS Details
                            </span>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-sky-600 dark:text-sky-400 transition-transform ${
                              expandedPartnerFos[application.id]
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                        {expandedPartnerFos[application.id] && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-2 text-xs space-y-1 mt-2">
                            {application.lead.sourcing_channel_contact_name && (
                              <div className="flex items-center gap-2">
                                <User className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                <span className="text-gray-900 dark:text-white font-semibold">
                                  {application.lead.sourcing_channel_contact_name}
                                </span>
                              </div>
                            )}
                            {application.lead.sourcing_channel_contact_phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                <span className="text-gray-700 dark:text-gray-300">
                                  {application.lead.sourcing_channel_contact_phone}
                                </span>
                              </div>
                            )}
                            {application.lead.sourcing_channel_contact_email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                <span className="text-gray-700 dark:text-gray-300 truncate">
                                  {application.lead.sourcing_channel_contact_email}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  }

                  {/* Disbursement Details */}
                  {application.disbursementdetails &&
                  application.disbursementdetails.length > 0 &&
                  application.disbursementdetails.some(d => d.transaction_details) && (
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-2xl p-3 mb-3 border border-emerald-100 dark:border-emerald-800/30">
                      <button
                        onClick={() =>
                          setExpandedDisbursements((prev) => ({
                            ...prev,
                            [application.id]: !prev[application.id],
                          }))
                        }
                        className="flex items-center justify-between w-full gap-2 mb-2"
                      >
                        <div className="flex items-center gap-2">
                          <Currency className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Disbursement Details ({application.disbursementdetails.length}) • ₹{application.disbursementdetails.reduce((sum, d) => sum + Number(d.disbursement_amount || 0), 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-emerald-600 dark:text-emerald-400 transition-transform ${
                            expandedDisbursements[application.id]
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </button>
                      {expandedDisbursements[application.id] && (
                        <div className="space-y-2">
                        {application.disbursementdetails.map((disbursement, index) => (
                          <div
                            key={index}
                            className="bg-white dark:bg-gray-800 rounded-xl p-3 text-xs space-y-2"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 mb-0.5">
                                  Amount
                                </p>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">
                                  ₹{Number(disbursement.disbursement_amount).toLocaleString('en-IN')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-0.5">
                                  Mode
                                </p>
                                <p className="text-xs font-semibold text-gray-900 dark:text-white">
                                  {disbursement.disbursement_mode}
                                </p>
                              </div>
                            </div>
                            {disbursement.disbursement_to_name && (
                              <div>
                                <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-0.5">
                                  To: {disbursement.disbursement_to}
                                </p>
                                <p className="text-xs text-gray-900 dark:text-white">
                                  {disbursement.disbursement_to_name}
                                </p>
                              </div>
                            )}
                            {disbursement.transaction_details && (
                              <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                {disbursement.transaction_details.dt_utr && (
                                  <div className="mb-1">
                                    <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                                      UTR
                                    </p>
                                    <p className="text-xs font-mono text-gray-900 dark:text-white">
                                      {disbursement.transaction_details.dt_utr}
                                    </p>
                                  </div>
                                )}
                                {disbursement.transaction_details.dt_utr_updated_on && (
                                  <div>
                                    <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                                      Updated On
                                    </p>
                                    <p className="text-xs text-gray-900 dark:text-white">
                                      {formatDate(disbursement.transaction_details.dt_utr_updated_on)}
                                    </p>
                                  </div>
                                )}
                                {disbursement.transaction_details.dt_disbursement_date && (
                                  <div className="mt-1">
                                    <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                                      Disbursement Date
                                    </p>
                                    <p className="text-xs text-gray-900 dark:text-white">
                                      {formatDate(disbursement.transaction_details.dt_disbursement_date)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fee Charges */}
                  {application.feechargeswithoutsubventi &&
                  application.feechargeswithoutsubventi.length > 0 && (
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30 rounded-2xl p-3 mb-3">
                      <button
                        onClick={() =>
                          setExpandedFeeCharges((prev) => ({
                            ...prev,
                            [application.id]: !prev[application.id],
                          }))
                        }
                        className="flex items-center justify-between w-full gap-2 mb-2"
                      >
                        <div className="flex items-center gap-2">
                          <Currency className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Fee charges ({application.feechargeswithoutsubventi.length}) • ₹{application.feechargeswithoutsubventi.reduce((sum, fee) => sum + Number(fee.fee_amount || 0), 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-violet-600 dark:text-violet-400 transition-transform ${
                            expandedFeeCharges[application.id]
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </button>
                      {expandedFeeCharges[application.id] && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-gray-200 dark:border-gray-700">
                                <th className="text-left py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">
                                  Fee Code
                                </th>
                                <th className="text-right py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {application.feechargeswithoutsubventi.map((fee, index) => (
                                <tr
                                  key={index}
                                  className="border-b border-gray-100 dark:border-gray-700 last:border-0"
                                >
                                  <td className="py-2 px-2 font-semibold text-gray-900 dark:text-white">
                                    {fee.fee_code}
                                  </td>
                                  <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400">
                                    {fee.fee_amount}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) 
                  // : (
                  //   <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-gray-700/50 rounded-2xl p-3 mb-3 border border-gray-200 dark:border-gray-600">
                  //     <div className="flex items-center gap-2">
                  //       <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  //       <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  //         Application is not assigned
                  //       </span>
                  //     </div>
                  //   </div>
                  // )
                  }

                  {/* Pending Users */}
                  {application.pending_users &&
                  application.pending_users.length > 0 &&
                  application.app_status !== 'DISBURSED' &&
                  application.app_current_status_final !== 'Disbursed' && (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-2xl p-3 mb-3">
                      <button
                        onClick={() =>
                          setExpandedPendingUsers((prev) => ({
                            ...prev,
                            [application.id]: !prev[application.id],
                          }))
                        }
                        className="flex items-center justify-between w-full gap-2 mb-2"
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Pending Users ({application.pending_users.length})
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-amber-600 dark:text-amber-400 transition-transform ${
                            expandedPendingUsers[application.id]
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </button>
                      {expandedPendingUsers[application.id] && (
                        <div className="space-y-2">
                          {application.pending_users.map((user, index) => (
                            <div
                              key={index}
                              className="bg-white dark:bg-gray-800 rounded-xl p-2 text-xs"
                            >
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {user.name}
                              </p>
                              <p className="text-gray-600 dark:text-gray-400">
                                Role: {user.roles}
                              </p>
                              {user.modules && user.modules.length > 0 && (
                                <p className="text-gray-500 dark:text-gray-400">
                                  Modules: {user.modules.join(", ")}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) 
                  // : (
                  //   <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-gray-700/50 rounded-2xl p-3 mb-3 border border-gray-200 dark:border-gray-600">
                  //     <div className="flex items-center gap-2">
                  //       <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  //       <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  //         Application is not assigned
                  //       </span>
                  //     </div>
                  //   </div>
                  // )
                  }

                  {/* Last User */}
                  {application.last_user &&
                    application.last_user.length > 0 &&
                    application.app_status !== 'DISBURSED' &&
                    application.app_current_status_final !== 'Disbursed' && (
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-2xl p-3 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <UserCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Last User
                          </span>
                        </div>
                        <div className="space-y-2">
                          {application.last_user.map((user, index) => (
                            <div
                              key={index}
                              className="bg-white dark:bg-gray-800 rounded-xl p-2 text-xs"
                            >
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {user.name}
                              </p>
                              <p className="text-gray-600 dark:text-gray-400">
                                Role: {user.roles}
                              </p>
                              {user.modules && user.modules.length > 0 && (
                                <p className="text-gray-500 dark:text-gray-400">
                                  Modules: {user.modules.join(", ")}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Date and View Details Button */}
                  <div className="flex items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Created: {formatDate(application.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <button
                        onClick={() => navigate(`/applications/${application.id}`)}
                        className="px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-xs font-semibold hover:shadow-lg transition-all active:scale-95 flex items-center gap-1.5"
                      >
                        Doc Hub
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      {canShowDisbursementAction(application) && (
                        <button
                          onClick={() => navigate(`/applications/${application.id}/disbursement-request`)}
                          className="px-3 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl text-xs font-semibold hover:shadow-lg transition-all active:scale-95 flex items-center gap-1.5"
                        >
                          <Banknote className="w-3.5 h-3.5" />
                          Disbursement
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.lastPage > 1 && (
              <div className="mt-6 flex items-center justify-between bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-lg border border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold transition-all ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg active:scale-95"
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Page {currentPage} of {pagination.lastPage}
                  </span>
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.lastPage}
                  className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold transition-all ${
                    currentPage === pagination.lastPage
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg active:scale-95"
                  }`}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && applicationToCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 safe-bottom">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleCancelModalClose}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md mx-auto overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-rose-500 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    Cancel Application
                  </h3>
                  <p className="text-xs text-white/80">
                    #{applicationToCancel.id}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-semibold mb-1">Are you sure you want to cancel this application?</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      This action cannot be undone. Please provide a reason for cancellation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Application Info */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {applicationToCancel.customer?.profile_category === 'non-individual'
                        ? applicationToCancel.customer?.profile_organization_name
                        : applicationToCancel.customer?.profile_category === 'sole-proprietor'
                        ? applicationToCancel.customer?.profile_prop_name
                        : <>{applicationToCancel.customer?.profile_first_name}{" "}{applicationToCancel.customer?.profile_last_name}</>}
                    </p>
                    {applicationToCancel.customer?.profile_category && applicationToCancel.customer?.profile_category !== 'individual' && (
                      <p className="text-[10px] font-medium text-indigo-500 dark:text-indigo-400 capitalize">
                        {applicationToCancel.customer?.profile_category === 'non-individual' ? 'Non-Individual' : 'Sole Proprietor'}
                      </p>
                    )}
                    {applicationToCancel.customer?.profile_phone && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {applicationToCancel.customer?.profile_phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Remarks Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Cancellation Remarks *
                </label>
                <textarea
                  value={cancelRemarks}
                  onChange={(e) => setCancelRemarks(e.target.value)}
                  placeholder="Please provide a reason for cancelling this application..."
                  rows={4}
                  disabled={cancelling}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-2xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex gap-3">
              <button
                onClick={handleCancelModalClose}
                disabled={cancelling}
                className="flex-1 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-2xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={cancelling || !cancelRemarks.trim()}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-2xl font-semibold hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Confirm Cancel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Help Button */}
      <HelpButton />
    </div>
  );
}

export default Applications;
